import { useEffect, useRef } from "react";
import { SMSMessage } from "@/services/twilioSmsService";
import { useSMS } from "@/hooks/useSMS";

interface UseSMSRealtimeOptions {
  phoneNumber: string;
  onNewMessage?: (message: SMSMessage) => void;
  enabled?: boolean;
}

/**
 * Subscribe to live SMS for a Twilio number.
 *
 * Backed by SMSProvider's single shared `sms_messages` postgres_changes subscription
 * (RLS-scoped to the user's assigned numbers). Consumers just register a listener — no
 * component opens its own realtime channel, so multiple consumers (drawer + conversation)
 * can't create competing channels for the same number.
 */
export function useSMSRealtime({ phoneNumber, onNewMessage, enabled = true }: UseSMSRealtimeOptions) {
  const { addMessageListener } = useSMS();

  // Keep the latest callback in a ref so we register once per number and always call the
  // current handler (no re-register churn when onNewMessage identity changes each render).
  const cbRef = useRef(onNewMessage);
  cbRef.current = onNewMessage;

  useEffect(() => {
    if (!enabled || !phoneNumber) return;
    const unsubscribe = addMessageListener(phoneNumber, (message) => cbRef.current?.(message));
    return unsubscribe;
  }, [phoneNumber, enabled, addMessageListener]);

  return { isSubscribed: enabled && !!phoneNumber };
}
